/**
 * The **mech (PC) action vocabulary** — the RAW player-mech action catalog a player drives from
 * the MECH CONTROL surface, as a single data-driven table. The mech twin of
 * {@link "./npcActions".NPC_ACTIONS}, deliberately a *parallel* table rather than a shared one:
 * the PC verbs are a **different, wider** vocabulary than the NPC set (Core Book pp. 73–79). PCs
 * **can Brace and Overcharge** (NPCs cannot), and PC **Stabilize heals + clears conditions** (NPC
 * Stabilize only sheds heat/Exposed + reloads). Keeping the two tables separate lets each carry its
 * own RAW-faithful blurbs without a shared shape forcing a lowest-common wording.
 *
 * Like the NPC table, this is the **seam** between the UI and the mech turn engine: every entry
 * carries a {@link MechActionStatus}, and only the `"live"` ones are wired to a real resolution
 * path. The live set, and the UI shape each takes:
 *
 *   - **Skirmish** — the single-weapon attack. **Barrage** — the FULL attack, up to two weapons,
 *     via the inline `BarrageChooser` → one `request_barrage` bundle.
 *   - **Boost** — the move-budget top-up.
 *   - **Ram** (`RamChooser` → a `request_attack` with `weapon: "act_ram"` and the optional 1-space
 *     push as a pre-declared `ram_push` choice), **Grapple** (`GrappleChooser` → a `request_attack`
 *     with `weapon: "act_grapple"`, no up-front declaration), **Improvised Attack** (RAW Core p.71
 *     — a FULL melee action, no weapon needed, 1d6 kinetic on a hit; `ImprovisedAttackChooser` → a
 *     `request_attack` with `weapon: "act_improvised_attack"`, same click-a-target shape as Grapple).
 *   - **Overcharge** — a one-click free action firing `request_overcharge`, no chooser: self-only,
 *     escalating heat rolled by the authority.
 *   - **Quick Tech** (arm → pick one of {Bolster, Invade, Lock On, Scan} → pick a target; each
 *     option routes to its existing resolver via `quickTechIntent`) and **Full Tech** (arm → pick
 *     TWO Quick Tech options + targets, may repeat the same one, RAW Core p.71 → one
 *     `request_full_tech` bundle).
 *   - **Hide** (one-click, self-only, `request_hide`; no chooser — the authority auto-checks cover)
 *     and **Search** (arm → the in-panel `SearchChooser` lists only Hidden targets within this
 *     mech's frozen Sensors range → pick one → fire `request_search`; the RAW opposed contest —
 *     `resolveContest` + `clearHiddenSteps` — resolves server-side, revealing the target on a
 *     searcher win **or tie**).
 *   - **Prepare** (RAW Core pp. 71–72) — arm → the in-panel `PrepareChooser` collects the readied
 *     quick verb (Skirmish / Ram / Grapple / Quick Tech, derived from this mech's own bar per
 *     `projects/prepare-hold-browser-arming.md` D4), a weapon card for Skirmish, and the freeform
 *     "when X, then Y" trigger → one `prepare` COMMAND (not an intent), led by `activate_unit` when
 *     the room would have folded one.
 *   - **Disengage** — a one-click, self-only Full action firing `request_disengage`; no chooser, no
 *     legality gate. The authority lands a first-class `Disengaged` status that suppresses the
 *     engagement mid-move stop AND gates a reaction attack targeting the actor, both read off the
 *     SAME status.
 *   - The reactor cluster: **Shut Down** (`request_shutdown`), **Boot Up** (`request_boot_up`), and
 *     **Self-Destruct** (`request_self_destruct` — arms/re-arms the reactor-meltdown countdown,
 *     which detonates at the melting-down mech's own turn end once ticked to 0).
 *   - **Dismount** / **Eject** — one-click, self-only, firing `request_dismount`/`request_eject`;
 *     both spawn the on-foot pilot from the mech's frozen pilot block, mark the mech Unpiloted, and
 *     end its turn.
 *   - **Engage COMP/CON** (RAW Core p.71) — a one-click, self-only **Protocol** firing
 *     `request_engage_compcon`; no chooser: lands the persistent `COMP/CON Engaged`
 *     status, 0 action points, reachable only for a mech carrying a cascade-immune AI system
 *     (`tg_ai`+`tg_no_cascade`) — see {@link "./economy".hasCompConSystem}. **Take Back Control**
 *     (`request_take_back_compcon`, Core p.106) is its one-click Protocol undo, at the start of a
 *     later turn with the pilot aboard.
 *
 * The rest render as honest **"resolves manually — engine pending"** reference rows. As the mech
 * turn engine lands each action, flip its `status` to `"live"` and wire it — the table is the
 * single place that record lives.
 *
 * **Pure data** — no I/O, no React. The control surface (`frontend/.../CombatWindows.tsx`) reads it; it
 * must never be re-declared inline in a component.
 */

/** A mech action's action-economy cost class (RAW, Core p. 73). Free/Protocol actions vary, hence rare here. */
export type MechActionCost = "Quick" | "Full" | "Reaction" | "Free" | "Protocol";

/**
 * Whether a mech action is wired to a real resolution path today.
 * - `"live"` — resolves through an existing engine path (Skirmish → the shared attack-resolution flow).
 * - `"engine-pending"` — RAW-faithful reference only; the resolving engine lands as the mech turn engine does.
 */
export type MechActionStatus = "live" | "engine-pending";

/**
 * A short, RAW-grounded classifier for a mech action — the per-row "category chip" the control surface
 * renders beside the name (the action's *kind*, distinct from its economy `cost`, which the 3-column
 * QUICK / FULL / REACTION grid already conveys). Kept terse + uppercase for the mono chip.
 */
export type MechActionCategory = "ATTACK" | "MELEE" | "MOVE" | "TECH" | "STEALTH" | "DEFENSE" | "SUPPORT";

/**
 * How an action **resolves** — the seam the unified action registry keys the bar on,
 * the shape `QUICK_TECH_OPTIONS` already uses. A descriptor carries its `route` so one enumeration list
 * can mix core + loadout-granted actions:
 * - `"attack"` — the attack-resolution spine (`resolve` → to-hit/damage): Skirmish, Ram, Barrage,
 *   Grapple, Improvised Attack.
 * - `"activate"` — an overlay `action` effect through `resolveActivate` (the granted-action path; core
 *   Quick/Full Tech also route here).
 * - `"reducer"` — its own bespoke resolver/intent (Boost / Stabilize / Overcharge / Hide / Search /
 *   Disengage), represented as a descriptor.
 * - `"reaction"` — a reaction offer (Brace / Overwatch), Category B: rendered informationally,
 *   **never** entered into the activate list.
 */
export type MechActionRoute = "attack" | "activate" | "reducer" | "reaction";

/**
 * The kind of **base-derived value badge** a mech action carries (the prototype's per-row figure).
 * Resolved to a display string by {@link mechActionValueLabel} from the mech's base — never hardcoded
 * per row, never a live-combat number:
 * - `"speed"` — Boost: the mech's Speed (`SPD {speed}`).
 *
 * (PC accuracy scales off grit/systems, not a flat tier band, so accuracy badges are deferred to the
 * engine rather than faked here — only the always-true `speed` figure is shown today.)
 */
export type MechActionValueKind = "speed";

/**
 * The **one** place mech action value badges scale off the base. Resolves a {@link MechActionValueKind}
 * to its display string for a given speed (a static reference figure, not live combat math).
 */
export function mechActionValueLabel(kind: MechActionValueKind, ctx: { speed: number }): string {
  switch (kind) {
    case "speed":
      return `SPD ${ctx.speed}`;
  }
}

/** One RAW mech action: a stable id, its RAW name + cost class + category + short blurb + wiring status. */
export type MechAction = {
  /** Stable key (also the React list key); RAW name lowercased/underscored. */
  id: string;
  /** The RAW action name as printed (e.g. "Skirmish"). */
  name: string;
  /** The action-economy cost class (Quick / Full / Reaction / …) — drives the grid column. */
  cost: MechActionCost;
  /** The action's kind (ATTACK / MELEE / MOVE / TECH / …) — the per-row chip beside the name. */
  category: MechActionCategory;
  /** A short RAW blurb of the action's effect. */
  blurb: string;
  /** Whether it resolves through a live engine path today, or is reference-only. */
  status: MechActionStatus;
  /** How this action resolves — the seam the unified action enumerator tags core actions with. */
  route: MechActionRoute;
  /** Optional base-derived **value badge** kind (the prototype's per-row figure). When absent, no badge. */
  value?: MechActionValueKind;
};

/**
 * The RAW mech (PC) action catalog (Core Book pp. 73–79). Ordered Skirmish-first (the live attack),
 * then the remaining quick actions, the full actions, and the reactions. Mirrors the NPC table's shape
 * so the shared control-surface grid renders both unchanged.
 */
export const MECH_ACTIONS: readonly MechAction[] = [
  {
    id: "skirmish",
    name: "Skirmish",
    cost: "Quick",
    category: "ATTACK",
    blurb: "Attack with one weapon (any number of times its tags allow).",
    status: "live",
    route: "attack",
  },
  {
    id: "boost",
    name: "Boost",
    cost: "Quick",
    category: "MOVE",
    blurb: "Move a number of spaces up to your Speed (in addition to your standard move).",
    status: "live",
    route: "reducer",
    value: "speed",
  },
  {
    id: "grapple",
    name: "Grapple",
    cost: "Quick",
    category: "MELEE",
    blurb: "Melee attack vs. an adjacent character; on a hit, both are Grappled to each other.",
    status: "live",
    route: "attack",
  },
  {
    id: "ram",
    name: "Ram",
    cost: "Quick",
    category: "MELEE",
    blurb: "Melee attack vs. an adjacent character of your size or smaller; on a hit, knock Prone and optionally 1 space back.",
    status: "live",
    route: "attack",
  },
  {
    id: "improvised_attack",
    name: "Improvised Attack",
    cost: "Full",
    category: "MELEE",
    blurb: "Melee attack vs. an adjacent character; on a hit, 1d6 kinetic damage. No weapon needed.",
    status: "live",
    route: "attack",
  },
  {
    id: "hide",
    name: "Hide",
    cost: "Quick",
    category: "STEALTH",
    blurb: "If you have cover or are obscured, become Hidden until you attack, are seen, or lose the condition.",
    status: "live",
    route: "reducer",
  },
  {
    id: "search",
    name: "Search",
    cost: "Quick",
    category: "TECH",
    blurb: "Contested Systems vs. the Agility of a Hidden character within Sensors; a win OR a tie reveals them (the initiator wins ties).",
    status: "live",
    route: "reducer",
  },
  {
    id: "quick_tech",
    name: "Quick Tech",
    cost: "Quick",
    category: "TECH",
    blurb: "Lock On, Invade (tech attack vs. E-DEF), or use a Quick Tech option from your loadout.",
    status: "live",
    route: "activate",
  },
  {
    id: "prepare",
    name: "Prepare",
    cost: "Quick",
    // SUPPORT, not a SETUP member of its own: `MechActionCategory` has no SETUP and the union is
    // not widened for one row (`projects/prepare-hold-browser-arming.md` D1). The NPC table's own
    // Prepare row reads "SETUP" because `NpcActionCategory` happens to carry that member.
    category: "SUPPORT",
    blurb: "Ready any other quick action with a \"when X, then Y\" trigger; until the start of your next turn you may take it as a reaction when the trigger fires.",
    status: "live",
    route: "reducer",
  },
  {
    id: "barrage",
    name: "Barrage",
    cost: "Full",
    category: "ATTACK",
    blurb: "Attack with up to two weapons (or one Superheavy); attacks may be split across targets.",
    status: "live",
    route: "attack",
  },
  {
    id: "full_tech",
    name: "Full Tech",
    cost: "Full",
    category: "TECH",
    blurb: "Use a Full Tech option from your loadout, or take two Quick Tech actions (may repeat the same one).",
    status: "live",
    route: "activate",
  },
  {
    id: "stabilize",
    name: "Stabilize",
    cost: "Full",
    category: "SUPPORT",
    blurb: "Cool down (clear all heat + Exposed) OR spend 1 Repair to restore all HP; then choose one: reload Loading weapons, clear Burn, clear a condition on you, or end a save-ending effect.",
    status: "live",
    route: "reducer",
  },
  {
    id: "shut_down",
    name: "Shut Down",
    cost: "Quick",
    category: "SUPPORT",
    blurb: "Clear all heat + Exposed, end tech-caused statuses, and become Shut Down (Stunned indefinitely + immune to all tech) until you Boot Up.",
    status: "live",
    route: "reducer",
  },
  {
    id: "boot_up",
    name: "Boot Up",
    cost: "Full",
    category: "SUPPORT",
    blurb: "Remove the Shut Down status — the one action a shut-down mech may still take.",
    status: "live",
    route: "reducer",
  },
  {
    id: "self_destruct",
    name: "Self-Destruct",
    cost: "Quick",
    category: "SUPPORT",
    blurb: "Arm your reactor to explode at the end of your next turn, or one of your next two rounds (your choice).",
    status: "live",
    route: "reducer",
  },
  {
    id: "disengage",
    name: "Disengage",
    cost: "Full",
    category: "MOVE",
    blurb: "For the rest of the turn your movement doesn't provoke reactions and ignores Engagement.",
    status: "live",
    route: "reducer",
  },
  {
    id: "dismount",
    name: "Dismount",
    cost: "Full",
    category: "SUPPORT",
    blurb: "Your pilot climbs out into an adjacent free space; you become Unpiloted and your turn ends.",
    status: "live",
    route: "reducer",
  },
  {
    id: "eject",
    name: "Eject",
    cost: "Quick",
    category: "SUPPORT",
    blurb: "Your pilot is flung to a space within 6; you become Unpiloted + Impaired (locked out of Eject until Full Repair) and your turn ends.",
    status: "live",
    route: "reducer",
  },
  {
    id: "overcharge",
    name: "Overcharge",
    cost: "Free",
    category: "SUPPORT",
    blurb: "Once per turn, take an additional Quick action by taking escalating heat (1 / 1d3 / 1d6 / 1d6+4).",
    status: "live",
    route: "reducer",
  },
  {
    id: "engage_compcon",
    name: "Engage COMP/CON",
    cost: "Protocol",
    category: "SUPPORT",
    blurb: "Give control of your mech to its COMP/CON: you drive its actions on your turn, but your talents and core bonuses don't apply while it's in control.",
    status: "live",
    route: "reducer",
  },
  {
    id: "take_back_compcon",
    name: "Take Back Control",
    cost: "Protocol",
    category: "SUPPORT",
    blurb: "At the start of a later turn, with your pilot aboard, take control of your mech back from its COMP/CON.",
    status: "live",
    route: "reducer",
  },
  {
    id: "overwatch",
    name: "Overwatch",
    cost: "Reaction",
    category: "ATTACK",
    blurb: "When a character starts moving within threat range, Skirmish against them as a reaction.",
    status: "engine-pending",
    route: "reaction",
  },
  {
    id: "brace",
    name: "Brace",
    cost: "Reaction",
    category: "DEFENSE",
    blurb: "When you are hit and damage is rolled (1/round): count as having Resistance to the triggering attack, and until the end of your next turn all other attacks against you are at +1 Difficulty.",
    status: "live",
    route: "reaction",
  },
];
